<template>
  <!-- A v-menu, not a v-tooltip, and for the same reason the citation panel
       beside the protocol name is one: a Vuetify tooltip is not interactive, so
       moving the pointer off the button to press Copy inside it dismisses the
       thing you were reaching for.

       Click only, unlike that panel. This one is a footer ACTION sitting in a
       row with Save; a list that unfurls whenever the pointer crosses the
       toolbar on its way to Save would be in the way of the button it covers. -->
  <v-menu
    v-model="open"
    location="top start"
    :close-on-content-click="false"
    max-width="440"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        variant="outlined"
        rounded="xl"
        size="small"
        class="footer-action sources-open"
        :disabled="!sources.length"
      >
        {{ $t('userAccount.clinical.sources') }}
      </v-btn>
    </template>

    <v-card rounded="lg" elevation="4" class="sources-card pa-4">
      <div class="d-flex align-start justify-space-between ga-3 mb-2">
        <p class="text-subtitle-2 mb-0">{{ $t('userAccount.clinical.sources') }}</p>

        <!-- Icon only, label in `title`/`aria-label`, and the check swap
             carries the confirmation a caption would otherwise give. Inline
             SVG for the same reason as everywhere else on these two screens:
             both Font Awesome sources here are SUBSETS, `fa-copy` is in
             neither, and a missing name renders a dashed-circle placeholder
             with no error anywhere. -->
        <v-btn
          icon
          variant="text"
          size="small"
          class="flex-shrink-0 mt-n1 mr-n2"
          :title="copyLabel"
          :aria-label="copyLabel"
          @click="copySources"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline v-if="copied" points="20 6 9 17 4 12" />
            <template v-else>
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </template>
          </svg>
        </v-btn>
      </div>

      <ol class="source-list">
        <li v-for="source in sources" :key="source.key" class="source-item">
          <div class="d-flex flex-wrap align-baseline ga-2">
            <span class="text-body-2 font-weight-medium">
              [{{ source.number }}] {{ source.publisher }}
            </span>
            <!-- "Our policy" in warning colour against a guideline's blue. A
                 ceiling we invented and a national recommendation are the two
                 things a reader must never confuse, and stripped of colour
                 they read identically. -->
            <v-chip
              size="x-small"
              variant="tonal"
              :color="source.kind === 'product-policy' ? 'warning' : '#2563eb'"
            >
              {{ kindLabel(source.kind) }}
            </v-chip>
          </div>
          <p class="text-body-2 mt-1 mb-0">
            <a v-if="source.url" :href="source.url" target="_blank" rel="noopener noreferrer">
              {{ source.reference }}
            </a>
            <span v-else>{{ source.reference }}</span>
          </p>
          <p v-if="source.note" class="text-caption text-medium-emphasis mt-1 mb-0">
            {{ source.note }}
          </p>
        </li>
      </ol>
    </v-card>
  </v-menu>
</template>

<script setup>
/**
 * The protocol's receipts, behind one footer button.
 *
 * They used to be a folded section at the bottom of the settings card, and the
 * first-run dialog had no way to see them at all — so the screen that ASKS a
 * pharmacy to accept a set of limits was the one that could not show who wrote
 * them. One button, both screens, same list.
 */
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { showNotification } from '../../stores/notification'

const props = defineProps({
  /** From useClinicalSources().sourceList — already numbered in reading order. */
  sources: { type: Array, default: () => [] },
  /** From useClinicalSources().sourcesText — the plain-text block to copy. */
  text: { type: String, default: '' },
  /** From useClinicalSources().kindLabel. */
  kindLabel: { type: Function, required: true }
})

const { t } = useI18n()

const open = ref(false)
const copied = ref(false)

/** Names the icon-only button, and doubles as its "Copied" confirmation. */
const copyLabel = computed(() =>
  copied.value
    ? t('userAccount.clinical.citation.copied')
    : t('userAccount.clinical.citation.copySources')
)

async function copySources() {
  if (!props.text) return
  try {
    // Clipboard API needs a secure context; the SPA is https everywhere except
    // a plain-http localhost, which browsers already treat as secure. A failure
    // here is therefore a permission refusal, not a missing feature — so say so
    // rather than falling back to a hidden textarea and execCommand, which is
    // deprecated and fails silently in its own ways.
    await navigator.clipboard.writeText(props.text)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    showNotification(t('userAccount.clinical.citation.copyFailed'), 'error')
  }
}
</script>

<style scoped>
/* Tall enough to be worth opening, short enough that the toolbar it springs
   from stays on screen behind it. */
.sources-card {
  max-height: 60vh;
  overflow-y: auto;
}

.source-list {
  margin: 0;
  padding-left: 1.25rem;
  /* The numbers are printed inside each item, as `[n]`, in the plain-text
     block the copy button hands over. An ol's own counter would put a second,
     differently-formatted number in front of them. */
  list-style: none;
}

.source-item + .source-item {
  margin-top: 1rem;
}

.source-item {
  margin-left: -1.25rem;
  border-radius: 8px;
  padding: 4px 8px;
}
</style>
